import React from 'react';
import styled from 'styled-components';
import { APP_CONFIG, LAST_UPDATED_TERMS } from '../config/constants';

const TermsSection = styled.section`
  background: transparent;
  min-height: calc(100vh - 80px);
  padding: 4rem 0;
`;

const TermsTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  font-weight: 700;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  margin-bottom: 3rem;
  font-style: italic;
`;

const TermsContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 3rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  h2 {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.5rem;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.1rem;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.8;
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.8;
    margin-bottom: 0.5rem;
  }

  strong {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
  }

  a {
    color: rgba(255, 255, 255, 0.95);
    text-decoration: none;
    font-weight: 500;
    border-bottom: 1px solid transparent;
    transition: all 0.3s;

    &:hover {
      text-decoration: underline;
      border-bottom-color: rgba(255, 255, 255, 0.95);
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;


const Terms = () => {
  return (
    <TermsSection>
      <div className="container">
        <TermsTitle>Terms of Service</TermsTitle>
        <LastUpdated>Last Updated: {LAST_UPDATED_TERMS}</LastUpdated>
        <TermsContent>
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing and using {APP_CONFIG.name} (the "App"), you agree to be bound by these Terms of Service (the "Terms"). If you do not agree to these Terms, please do not use the App. {APP_CONFIG.company} ("we," "us," "our," or "Company") reserves the right to modify these Terms at any time. Your continued use of the App following any changes constitutes your acceptance of the new Terms.
          </p>

          <h2>2. Use License</h2>
          <p>
            {APP_CONFIG.company} grants you a limited, non-exclusive, non-transferable, revocable license to use the App for personal, non-commercial purposes only. You may not:
          </p>
          <ul>
            <li>Reproduce, distribute, or transmit the App or its content</li>
            <li>Modify, adapt, or create derivative works from the App</li>
            <li>Reverse engineer, decompile, or disassemble the App</li>
            <li>Attempt to gain unauthorized access to the App or its systems</li>
            <li>Use the App through automated means, scripts, bots, or any method other than the App's normal user interface</li>
            <li>Remove or alter any proprietary notices or labels on the App</li>
          </ul>

          <h2>3. Intellectual Property Rights</h2>
          <p>
            The App, including all content, features, design, and functionality, is the exclusive property of {APP_CONFIG.company} and is protected by copyright, trademark, and other intellectual property laws. You retain all rights to the check-in data and content you create and upload to the App. We retain all rights to the App itself, including its code, design, and features.
          </p>

          <h2>4. User Responsibilities</h2>
          <p>You are responsible for:</p>
          <ul>
            <li>Creating and maintaining the confidentiality of your account credentials</li>
            <li>All activity that occurs under your account</li>
            <li>Ensuring your use of the App complies with all applicable laws and regulations</li>
            <li>Your interactions with other users of the App</li>
          </ul>

          <h2>5. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Engage in harassment, abuse, threatening behavior, or hate speech</li>
            <li>Impersonate or misrepresent your identity</li>
            <li>Share your account credentials with others or allow others to access your account</li>
            <li>Upload, distribute, or transmit any illegal, harmful, or offensive content</li>
            <li>Attempt to interfere with or disrupt the App's functionality or security</li>
            <li>Engage in any other illegal or unethical behavior</li>
          </ul>
          <p>
            {APP_CONFIG.company} reserves the right to investigate violations and take appropriate action, including account suspension or termination, at our sole discretion.
          </p>

          <h2>6. Service Disclaimer and Best Effort</h2>
          <p>
            <strong>{APP_CONFIG.name} is provided on an "as-is" basis.</strong> The App is designed to help users monitor the wellbeing of people they care about through automated check-in notifications. However, you acknowledge and agree to the following:
          </p>
          <ul>
            <li>
              <strong>Best Effort Delivery:</strong> Notifications are delivered on a best-effort basis. We do not guarantee on-time delivery or that notifications will be received at all. Delays may occur due to network conditions, device settings, operating system limitations, or other factors beyond our control.
            </li>
            <li>
              <strong>No Guarantees of Wellbeing:</strong> The App is intended to provide peace of mind only. It is not a substitute for direct communication or professional care. We do not guarantee that check-in data accurately reflects a person's wellbeing or safety. If you require more frequent or immediate confirmation of someone's wellbeing, you should manage that through other means of communication.
            </li>
            <li>
              <strong>Your Responsibility:</strong> You are solely responsible for determining whether the App meets your needs and for taking appropriate action based on missed check-ins or other information provided by the App.
            </li>
          </ul>

          <h2>7. Disclaimer of Warranties</h2>
          <p>
            THE APP IS PROVIDED "AS-IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. {APP_CONFIG.company} DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>{APP_CONFIG.company} DOES NOT WARRANT THAT:</p>
          <ul>
            <li>The App will be uninterrupted, error-free, or free from harmful components</li>
            <li>Any defects in the App will be corrected</li>
            <li>The App will meet your expectations or requirements</li>
            <li>Your data will be secure or permanently preserved</li>
          </ul>

          <h2>8. Limitation of Liability</h2>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, {APP_CONFIG.company} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES (EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES).
          </p>
          <p>
            THIS LIMITATION APPLIES TO ALL CLAIMS, WHETHER BASED ON WARRANTY, CONTRACT, TORT, STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT {APP_CONFIG.company} HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES OR LIMITATION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THESE LIMITATIONS MAY NOT APPLY TO YOU.
          </p>

          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless {APP_CONFIG.company} and its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from or related to:
          </p>
          <ul>
            <li>Your use of the App</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any applicable law or regulation</li>
            <li>Your breach of any third-party rights</li>
            <li>Any content you upload or share through the App</li>
          </ul>

          <h2>10. Subscriptions and Billing</h2>
          <h3>Free Trial</h3>
          <p>
            {APP_CONFIG.name} offers a free trial period of one (1) week for new subscribers. During the free trial period, you will have full access to all premium features of the App at no charge.
          </p>
          <ul>
            <li>The free trial begins when you first subscribe to a premium plan.</li>
            <li>At the end of the one-week free trial period, your subscription will automatically convert to a paid subscription unless you cancel before the trial expires.</li>
            <li>You will be charged the subscription fee at the end of the free trial period if you have not canceled.</li>
            <li>You can cancel your subscription at any time during the free trial period through your Apple account settings to avoid being charged.</li>
            <li>If you cancel during the free trial, you will retain access to premium features until the end of the trial period, after which your access will revert to the free tier (if available).</li>
            <li>Each user is eligible for one free trial per account. {APP_CONFIG.company} reserves the right to limit or restrict free trial eligibility at its discretion.</li>
          </ul>
          <h3>Subscriptions</h3>
          <ul>
            <li>Subscriptions are facilitated through Apple's App Store and are governed by Apple's terms and conditions.</li>
            <li>Subscription prices are displayed in the App and may vary by region.</li>
            <li>Subscriptions will automatically renew unless canceled before the renewal date.</li>
            <li>You can manage or cancel your subscription through your Apple account settings.</li>
            <li>Refund requests related to subscriptions should be directed to Apple through the App Store.</li>
          </ul>

          <h2>11. Data Retention and Account Termination</h2>
          <ul>
            <li>If you delete your account, all associated data will be immediately and permanently removed from our systems.</li>
            <li>{APP_CONFIG.company} reserves the right to terminate your account at any time, for any reason, without prior notice or liability.</li>
            <li>Upon account termination, your access to the App will be revoked.</li>
          </ul>

          <h2>12. Privacy</h2>
          <p>
            Your use of the App is governed by our Privacy Policy. Please review our Privacy Policy to understand our privacy practices.
          </p>

          <h2>13. Third-Party Services</h2>
          <p>
            The App may integrate with or rely on third-party services, including but not limited to push notification services, email providers, and cloud storage. Your use of these services is subject to their respective terms and policies. {APP_CONFIG.company} is not responsible for third-party services or their practices.
          </p>

          <h2>14. Links to Third-Party Sites</h2>
          <p>
            The App may contain links to third-party websites or services. {APP_CONFIG.company} is not responsible for the content, accuracy, or practices of third-party sites. Your use of third-party sites is at your own risk and subject to their terms and conditions.
          </p>

          <h2>15. Modifications to the App</h2>
          <p>
            {APP_CONFIG.company} reserves the right to modify, suspend, or discontinue the App or any portion thereof at any time, without notice or liability. We may also update the App's features, functionality, or content at our discretion.
          </p>

          <h2>16. Governing Law and Jurisdiction</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States and the State of California, without regard to its conflict of laws provisions. You agree that any legal action or proceeding arising from or related to these Terms or the App shall be brought exclusively in the courts located in California, and you consent to the jurisdiction and venue of such courts.
          </p>

          <h2>17. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable, such provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if such modification is not possible, the provision shall be severed. The remaining provisions shall remain in full force and effect.
          </p>

          <h2>18. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and {APP_CONFIG.company} regarding the App and supersede all prior and contemporaneous agreements, understandings, and communications, whether written or oral.
          </p>

          <h2>19. Contact Us</h2>
          <p>If you have questions about these Terms of Service, please contact us at:</p>
          <p>Email: <a href={`mailto:${APP_CONFIG.email}`}>{APP_CONFIG.email}</a></p>

        </TermsContent>
      </div>
    </TermsSection>
  );
};

export default Terms;